import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, Info, ListChecks } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Toolbar } from '@/components/ui/Toolbar'
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable'
import { Pagination } from '@/components/ui/Pagination'
import { Button } from '@/components/ui/Button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { InfoPopover } from '@/components/ui/InfoPopover'
import { StatusDot } from '@/components/ui/StatusDot'
import { useCrudList } from '@/hooks/useCrudList'
import { useDataTableExport } from '@/hooks/useDataTableExport'
import { useStatusFilter } from '@/hooks/useStatusFilter'
import { useAuthority } from '@/hooks/useAuthority'
import {
  CommissionStructuresService,
  type CommissionStructure,
  type CommissionStructureRule,
  type CommissionStructuresApi,
} from '@/services/modules/CommissionStructuresService'
import { toast } from '@/components/ui/Toast'

export interface CommissionListConfig {
  service: CommissionStructuresApi
  title: string
  subtitle: string
  crumbs: { label: string; to?: string }[]
  newPath: string
  editPath: (id: string) => string
  tableId: string
  exportFilename: string
  /**
   * Permissões (authority) exigidas pelas rotas de escrita. Só o admin tem:
   * as rotas do parceiro (`/partner/...`) não usam authority e o `useAuthority`
   * nunca concede nada a parceiro — sem chave, a ação fica liberada.
   */
  authority?: { create: string; edit: string; delete: string }
}

const ADMIN_CFG: CommissionListConfig = {
  service: CommissionStructuresService,
  title: 'Tabelas de comissão',
  subtitle: 'Estruturas comerciais por parcela e tipo de usuário',
  crumbs: [{ label: 'Configurações', to: '/settings' }, { label: 'Comercial' }, { label: 'Comissionamento' }],
  newPath: '/settings/commercial/commission-structures/novo',
  editPath: (id) => `/settings/commercial/commission-structures/${id}/editar`,
  tableId: 'settings.commercial.commissionstructures',
  exportFilename: 'tabelas-comissao-comercial',
  authority: {
    create: 'commercial.commission_structures.create',
    edit:   'commercial.commission_structures.edit',
    delete: 'commercial.commission_structures.delete',
  },
}

export function CommissionStructuresListPage({ cfg = ADMIN_CFG }: { cfg?: CommissionListConfig } = {}) {
  const navigate = useNavigate()
  const [reload, setReload] = useState(0)
  const sf = useStatusFilter({ searchPlaceholder: 'Buscar tabela' })
  const { setPage, size, setSize, rows, meta, loading } = useCrudList(cfg.service, { size: 25, reloadKey: reload, filters: sf.filters })
  const [openDelete, setOpenDelete] = useState<CommissionStructure | null>(null)
  const { can } = useAuthority()
  const canCreate = !cfg.authority || can(cfg.authority.create)
  const canEdit = !cfg.authority || can(cfg.authority.edit)
  const canDelete = !cfg.authority || can(cfg.authority.delete)

  // Excel mantém colunas separadas — útil pra filtros/relatórios.
  const exp = useDataTableExport({
    filename: cfg.exportFilename,
    service: cfg.service,
    filters: sf.filters,
    columns: [
      { header: 'Nome',            accessor: (s: CommissionStructure) => s.name },
      { header: 'Parcelas',        accessor: (s: CommissionStructure) => s.monthlyInstallments ?? 'Sem limite' },
      { header: 'Meta vendas/mês', accessor: (s: CommissionStructure) => s.monthlyPlansNeedSold ?? 'Sem meta' },
      { header: 'Regras',          accessor: (s: CommissionStructure) => s.rules?.length ?? 0 },
      { header: 'Status',          accessor: (s: CommissionStructure) => s.status },
    ],
  })

  async function handleDelete() {
    if (!openDelete) return
    try { await cfg.service.destroy(openDelete.id); toast.success({ title: 'Removida', description: 'Tabela de comissão removida.' }); setOpenDelete(null); setReload(v => v + 1) }
    catch { toast.error({ title: 'Erro', description: 'Não foi possível remover.' }) }
  }

  const columns: DataTableColumn<CommissionStructure>[] = [
    {
      key: 'name', header: 'Nome', sortable: true, alwaysVisible: true,
      render: s => <span className="font-medium text-gray-800">{s.name}</span>,
    },
    {
      key: 'config', header: 'Configuração', align: 'center', className: 'w-40',
      render: s => (
        <InfoPopover
          ariaLabel="Ver configuração"
          title="Configuração da tabela"
          align="center"
          trigger={
            <span className="inline-flex items-center gap-1.5 text-[12px] text-gray-600 hover:text-gray-900 hover:underline">
              <Info size={14} className="text-blue-500" />
              Detalhes
            </span>
          }
        >
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5">
            <dt className="text-gray-500">Parcelas:</dt>
            <dd className="text-gray-800 font-medium">
              {s.monthlyInstallments != null ? `${s.monthlyInstallments}x` : <span className="text-gray-400 italic">Sem limite</span>}
            </dd>
            <dt className="text-gray-500">Meta vendas/mês:</dt>
            <dd className="text-gray-800 font-medium">
              {s.monthlyPlansNeedSold != null ? s.monthlyPlansNeedSold : <span className="text-gray-400 italic">Sem meta</span>}
            </dd>
          </dl>
        </InfoPopover>
      ),
    },
    {
      key: 'rules', header: 'Regras', align: 'center', className: 'w-28',
      render: s => <RulesPopover rules={s.rules ?? []} />,
    },
    {
      key: 'status', header: 'Status', align: 'center', className: 'w-24',
      render: s => (
        <StatusDot value={s.status} />
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title={cfg.title}
        subtitle={cfg.subtitle}
        crumbs={cfg.crumbs}
        actions={canCreate ? (
          <Button onClick={() => navigate(cfg.newPath)} iconLeft={<Plus size={14} />}>
            Nova tabela
          </Button>
        ) : undefined}
      />
      <Toolbar onFiltersClick={sf.open} activeFiltersCount={sf.activeCount} />
      <DataTable
        tableId={cfg.tableId}
        columns={columns}
        rows={rows}
        loading={loading}
        getRowKey={r => r.id}
        exportable
        onExportPage={() => exp.exportPage(rows)}
        onExportAll={exp.exportAll}
        rowActions={(s) => [
          ...(canEdit ? [{ key: 'edit',   label: 'Editar',  icon: <Pencil size={14} />, onClick: () => navigate(cfg.editPath(s.id)) }] : []),
          ...(canDelete ? [{ key: 'delete', label: 'Remover', icon: <Trash2 size={14} />, danger: true, divider: canEdit, onClick: () => setOpenDelete(s) }] : []),
        ]}
      />
      <Pagination meta={meta} onChange={setPage} size={size} onSizeChange={setSize} />

      <ConfirmDialog
        open={!!openDelete}
        onClose={() => setOpenDelete(null)}
        onConfirm={handleDelete}
        title="Remover tabela de comissão?"
        description={`Tem certeza que deseja remover "${openDelete?.name}"? As regras associadas também serão removidas. Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        variant="danger"
      />
      {sf.drawer}
    </>
  )
}

function RulesPopover({ rules }: { rules: CommissionStructureRule[] }) {
  const count = rules.length

  return (
    <InfoPopover
      ariaLabel="Ver regras"
      title={`${count} ${count === 1 ? 'regra cadastrada' : 'regras cadastradas'}`}
      align="center"
      minWidth={300}
      maxWidth={420}
      trigger={
        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 text-blue-700 px-2.5 py-0.5 text-[12px] font-medium hover:bg-blue-100">
          <ListChecks size={12} />
          {count}
        </span>
      }
    >
      {count === 0 ? (
        <p className="text-gray-400 italic text-center py-2">Nenhuma regra cadastrada.</p>
      ) : (
        <ul className="divide-y divide-gray-100 -mx-3 -my-2">
          {[...rules]
            .sort((a, b) => a.installmentFrom - b.installmentFrom)
            .map(r => (
              <li key={r.id} className="px-3 py-2 flex items-center justify-between gap-3">
                <span className="text-gray-700">
                  {r.installmentAction === 'A PARTIR DE'
                    ? `A partir da ${r.installmentFrom}ª parcela`
                    : `${r.installmentFrom}ª – ${r.installmentTo}ª parcela`}
                </span>
                <span className="font-medium text-gray-900 shrink-0">
                  {r.commissionType === 'PORCENTAGEM'
                    ? `${r.value.toFixed(2).replace('.', ',')}%`
                    : `R$ ${r.value.toFixed(2).replace('.', ',')}`}
                </span>
              </li>
            ))}
        </ul>
      )}
    </InfoPopover>
  )
}
